// Fallback bounds only, matching GiftCard::MIN_VALUE / MAX_VALUE in shop-backend (1000 /
// 100000 minor units = €10 / €1000). Used only when the company's region carries no
// gift_card limits of its own — see useGiftCardLimits, the actual read path.
export const MIN_GIFT_CARD_VALUE = 10;
export const MAX_GIFT_CARD_VALUE = 1000;

// The preset ladder as fractions of its top rung — the historical €10 / €25 / €50 / €75 / €100 shape.
const LADDER = [0.1, 0.25, 0.5, 0.75, 1];

/**
 * Rounds to a "nice" amount relative to the value's own magnitude (half a power of ten: 1,250 →
 * 1,500, 37,500 → 40,000). An absolute step would flatten small values to zero, which is what
 * collapsed the ladder onto a single rung for regions with small numeric bounds.
 */
const roundToNiceStep = (value: number): number => {
  if (value <= 0) {
    return 0;
  }

  const step = Math.max(1, 10 ** Math.floor(Math.log10(value) + 1e-9) / 2);

  return Math.round(value / step) * step;
};

/**
 * Preset gift-card amounts, in minor units, always inside the region's own bounds.
 *
 * The top rung is the larger of ten times the minimum and a tenth of the maximum (both 10,000 for
 * the EUR defaults, so the historical ladder is unchanged), capped at the maximum. Anchoring on the
 * minimum as well as the maximum keeps several distinct rungs when a region's bounds are tight or
 * when only its minimum is raised; scaling by the maximum alone put every rung below the minimum
 * in those cases and clamped them all onto it.
 *
 * `max <= min` is a misconfigured region: the only (or no) legal value is the minimum, so that is
 * the single preset offered rather than a ladder that lies about the bounds.
 */
export const getGiftCardPresets = (min = MIN_GIFT_CARD_VALUE * 100, max = MAX_GIFT_CARD_VALUE * 100): number[] => {
  if (max <= min) {
    return [min];
  }

  const top = Math.min(Math.max(min * 10, max / 10), max);

  return Array.from(new Set(LADDER.map((fraction) => Math.min(Math.max(roundToNiceStep(top * fraction), min), max))));
};
